import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useAnalytics } from '@/shared/features/analytics/presentation/hooks/useAnalytics';
import type { ListingDesignItem, ListingFilter } from '../../domain/models/ProductListing';
import { buildFilterAppliedEventPayload } from '@/features/filters/utils/buildFilterAppliedEventPayload';
import { buildListingPageProductViewedPayload } from '../analytics/listingPageProductViewed';
import {
  buildPlpProductButtonClickedPayload,
  buildPlpProductCardClickedPayload,
} from '../analytics/plpProductClicked';
import { PLP_SORT_OPTIONS, PLP_PAGE_TYPE, type PlpSortByValue } from '../../constants';
import { parsePrice } from '@/shared/utils/priceFormatter';
import { facebookAddToWishlist } from '@/features/wishlist/presentation/analytics/facebookAddToWishlist';
import { facebookPlpViewed } from '../analytics/facebookPlpViewed';

export type PlpAnalyticsPageType =
  | typeof PLP_PAGE_TYPE.BROWSE
  | typeof PLP_PAGE_TYPE.SEARCH
  | 'stores'
  | typeof PLP_PAGE_TYPE.IMAGE_SEARCH;

export interface UsePlpAnalyticsParams {
  pageType: PlpAnalyticsPageType;
  listingHeader: string;
  collectionTags: string;
  currentSortBy: PlpSortByValue;
  totalCount?: number;
}

function sortLabelForValue(value: PlpSortByValue): string {
  const option = PLP_SORT_OPTIONS.find((o) => o.value === value);
  return option?.label ?? value;
}


export function usePlpAnalytics({
  pageType,
  listingHeader: listingHeaderRaw,
  collectionTags,
  currentSortBy,
  totalCount = 0,
}: UsePlpAnalyticsParams) {
  const listingHeader = listingHeaderRaw.trim();
  const { analytics } = useAnalytics();
  const scrollTrackedRef = useRef(false);
  const viewedProductIds = useMemo(() => new Set<number>(), []);
  const collectionPageViewTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (collectionPageViewTimeoutRef.current) {
        clearTimeout(collectionPageViewTimeoutRef.current);
      }
    };
  }, []);

  const trackPageView = useCallback((designs?: ListingDesignItem[]) => {
    if (!analytics) return;
    analytics.trackEvent('Product Listing Page Viewed', {
      collection_id: collectionTags,
      name: listingHeader,
    });
    analytics.logGoKwikSystemEvent('collection', {
      collection_id: collectionTags,
      name: listingHeader,
    });

    if (designs?.length) {
      facebookPlpViewed({
        designs,
        totalCount,
        sortBy: currentSortBy,
        listName: listingHeader,
      });
    }

    if (!listingHeader.toLowerCase().includes('collection') || totalCount <= 0) {
      return;
    }

    if (collectionPageViewTimeoutRef.current) {
      clearTimeout(collectionPageViewTimeoutRef.current);
    }

    collectionPageViewTimeoutRef.current = setTimeout(() => {
      analytics.logEventLogger({
        _eventId: 'collection_page_viewed',
        pageTitle: listingHeader,
        pageType,
        selectedTapDesignCount: totalCount,
        numberOfDesigns: totalCount,
      });
      collectionPageViewTimeoutRef.current = null;
    }, 2000);
  }, [analytics, collectionTags, listingHeader, pageType, totalCount, currentSortBy]);

  const trackScrollDown = useCallback(() => {
    if (!analytics || scrollTrackedRef.current) return;
    scrollTrackedRef.current = true;
    analytics.trackEvent(
      'BrowsePage Scroll down',
      { page: typeof window !== 'undefined' ? window.location.pathname : '' },
      { eventProviders: ['ga'] },
    );
  }, [analytics]);

  const trackProductClick = useCallback(
    (product: ListingDesignItem, scrollDepth?: number, horizontalPosition?: number) => {
      if (!analytics) return;
      analytics.logEventLogger(buildPlpProductCardClickedPayload(product));
      analytics.trackEvent('Product Clicked', {
        'Product Id': product.designId ? String(product.designId) : '',
        'Product Name': product.designName || '',
        'Category Name': product.categoryType || '',
        'MRP': product.price ? parsePrice(product.price) : 0,
        'Selling Price': product.discountedPrice ? parsePrice(product.discountedPrice) : 0,
        'Discount Price': product.discountValue ? parsePrice(product.discountValue) : 0,
        'Discount Percent': product.discountPercent ? parsePrice(String(product.discountPercent)) : 0,
        'Currency': 'INR',
        'Scroll depth': scrollDepth ?? 0,
        'Horizontal position': horizontalPosition ?? 0,
        'Offer Name': product.discountName ? product.discountName : '',
      });
    },
    [analytics],
  );

  const trackVideoClick = useCallback(
    (product: ListingDesignItem, productPosition: number) => {
      if (!analytics) return;
      analytics.logEventLogger(
        buildPlpProductButtonClickedPayload(product, 'play_button', productPosition),
      );
      analytics.trackEvent(
        'Video icon Clicked',
        { 'Product Id': product.designId, 'Product Name': product.designName },
        { eventProviders: ['ga'] },
      );
    },
    [analytics],
  );

  const trackWishlistClick = useCallback(
    (product: ListingDesignItem, productPosition: number) => {
      if (!analytics || pageType !== 'browse') return;
      analytics.logEventLogger(
        buildPlpProductButtonClickedPayload(product, 'wishlist', productPosition),
      );
    },
    [analytics, pageType],
  );

  const trackWishlistToggle = useCallback(
    (product: ListingDesignItem, action: 'add' | 'remove') => {
      if (!analytics) return;
      const eventName = action === 'add' ? 'Added To Wishlist' : 'Removed From Wishlist';
      analytics.trackEvent(eventName, {
        'Product Id': String(product.designId),
        'Product Name': product.designName,
        'Category Name': product.categoryType ?? '',
        'MRP': parsePrice(product.price),
        'Selling Price': parsePrice(product.discountedPrice ?? product.price),
        'Discount': parsePrice(product.discountValue),
        'Offer Name': product.discountName ?? '',
        'Currency': 'INR',
        'Color': product.metal ?? '',
        'Image': product.analyticsImageUrl ?? '',
        'Size': '',
      });

      if (action === 'add') {
        facebookAddToWishlist({
          designId: product.designId,
          designName: product.designName,
          designCategory: product.categoryType,
          price: product.discountedPrice ?? product.price,
          variantId: product.customizationId,
        });
      }
    },
    [analytics],
  );

  const trackFilterItemSelected = useCallback(
    (category: string, name: string) => {
      if (!analytics) return;
      analytics.trackEvent(
        `${category} option Selected`,
        { category, option: name },
        { eventProviders: ['ga'] },
      );
    },
    [analytics],
  );

  // Desktop: fires 'Filter Applied' on each filter item selection, same attribute shape as mobile
  const trackDesktopFilterItemSelected = useCallback(
    (payload: Record<string, string>) => {
      if (!analytics) return;
      analytics.trackEvent('Filter Applied', payload);
    },
    [analytics],
  );

  // GA-only: individual option check label
  const trackFilterOptionToggle = useCallback(
    (category: string, name: string) => {
      if (!analytics) return;
      analytics.trackEvent(
        `${category} option Selected`,
        { category, option: name },
        { eventProviders: ['ga'] },
      );
    },
    [analytics],
  );

  // Mobile Apply button tap — GA event + WebEngage 'Filter Applied' with per-category display names
  const trackFilterApply = useCallback(
    (filters: ListingFilter[], selectedOptions: Record<string, string[]>) => {
      if (!analytics) return;
      analytics.trackEvent('Filter Popup Apply', {}, { eventProviders: ['ga'] });
      const payload = buildFilterAppliedEventPayload(filters, selectedOptions);
      if (!payload) return;
      analytics.trackEvent('Filter Applied', payload);
      const subItemArr: string[] = Object.values(selectedOptions).flat();
      analytics.logEventLogger({
        _eventId: 'mw_plp_filtervalue_selected',
        button_clicked: 'apply',
        selected_options: selectedOptions,
        menuitem_selected: Object.keys(selectedOptions),
        submenuitem_selected: subItemArr,
      });
    },
    [analytics],
  );

  const trackFilterReset = useCallback(
    (filters: ListingFilter[], selectedOptions: Record<string, string[]>) => {
      if (!analytics) return;
      const payload: Record<string, string> = {};
      for (const category of Object.keys(selectedOptions)) {
        const names = selectedOptions[category];
        const filter = filters.find((f) => f.category === category);
        payload[category] = names
          .map((n) => filter?.items.find((i) => i.name === n)?.displayName ?? n)
          .join(',');
      }
      analytics.trackEvent('Reset Filter Clicked', payload);
      analytics.logEventLogger({
        _eventId: 'mw_plp_filtervalue_selected',
        button_clicked: 'reset',
      });
    },
    [analytics],
  );

  // GA-only: mobile back/close arrow
  const trackFilterClose = useCallback(() => {
    if (!analytics) return;
    analytics.trackEvent(
      'Filter Popup Back Arrow',
      {},
      { eventProviders: ['ga'] },
    );
  }, [analytics]);

  // GA-only: mobile accordion expand + server-side filter expand event
  const trackFilterCategoryExpand = useCallback(
    (category: string) => {
      if (!analytics) return;
      analytics.logEventLogger({
        _eventId: 'mw_plp_filtervalue_expanded',
        menu_item: category,
      });
      analytics.trackEvent(
        `${category} on Popup Expand Options`,
        { category },
        { eventProviders: ['ga'] },
      );
    },
    [analytics],
  );

  // GA-only: tapping a sort radio (draft selection, not yet applied)
  const trackSortOptionSelect = useCallback(
    (value: PlpSortByValue) => {
      if (!analytics) return;
      analytics.trackEvent(
        `Clicked Sort ${sortLabelForValue(value)}`,
        { sort_option: value },
        { eventProviders: ['ga'] },
      );
    },
    [analytics],
  );

  const trackSortApply = useCallback(
    (value: PlpSortByValue) => {
      if (!analytics) return;
      analytics.trackEvent('Sort Filter Applied', {
        'Filter Name': sortLabelForValue(value),
        previous_sort_option: currentSortBy,
        page_type: pageType,
      });
      analytics.logEventLogger({
        _eventId: 'sorted_product_list',
        existingSortOption: currentSortBy,
        sortOption: value,
        currentURL: typeof window !== 'undefined' ? window.location.href : '',
        pageType: 'browse',
      });
    },
    [analytics, currentSortBy, pageType],
  );

  // GA-only: cancel button in sort sheet + server-side cancel event
  const trackSortCancel = useCallback(() => {
    if (!analytics) return;
    analytics.trackEvent(
      'Clicked Sort Cancel',
      {},
      { eventProviders: ['ga'] },
    );
    analytics.logEventLogger({
      _eventId: 'plp_sort_option_selected',
      button_clicked: 'cancel',
      sortOption: currentSortBy,
    });
  }, [analytics, currentSortBy]);

  // WebEngage-only: pincode button tap
  const trackPincodeClick = useCallback((pincode: string) => {
    if (!analytics) return;
    analytics.trackEvent(
      'Enter Pincode CTA Clicked',
      { 'Pincode': pincode },
      { eventProviders: ['webengage'] },
    );
  }, [analytics]);

  // WebEngage-only: pincode form submitted
  const trackPincodeEntered = useCallback((pincode: string, isError: boolean) => {
    if (!analytics) return;
    analytics.trackEvent('Pincode Entered', {
      'Pincode': pincode,
      'Error Flag': isError,
    });
  }, [analytics]);

  // All providers: sort button tapped (opens sort sheet)
  const trackSortFilterClicked = useCallback(() => {
    if (!analytics) return;
    analytics.trackEvent('Sort Filter Clicked', {});
  }, [analytics]);

  // Mobile: filter button tapped (opens filter sheet)
  const trackFilterButtonClicked = useCallback(() => {
    if (!analytics) return;
    analytics.trackEvent('Filter Button Clicked', {});
  }, [analytics]);

  // Desktop: sort dropdown opened by hover
  const trackSortFilterHovered = useCallback(() => {
    if (!analytics) return;
    analytics.trackEvent('Sort Filter Hovered', {});
  }, [analytics]);

  // WebEngage-only: "Designs in Store" toggle
  const trackDesignsInStoreClick = useCallback((pincode: string) => {
    if (!analytics) return;
    analytics.trackEvent(
      'Designs In Store Button Clicked',
      { 'Pincode': pincode },
      { eventProviders: ['webengage'] },
    );
  }, [analytics]);

  const trackVideoCallCartAdd = useCallback(
    (product: ListingDesignItem, errorMsg?: string) => {
      if (!analytics || errorMsg) return;
      analytics.trackEvent('Added to Video Call Cart', {
        'Product Id': product.designId,
        'Product Name': product.designName,
        'MRP': parsePrice(product.price),
        'Selling Price': parsePrice(product.discountedPrice ?? product.price),
        'Discount': parsePrice(product.discountValue),
        'Offer Name': product.discountName ?? '',
        'Currency': 'INR',
      });
    },
    [analytics],
  );

  const trackViewDetailsClick = useCallback(
    (product: ListingDesignItem) => {
      if (!analytics) return;
      analytics.logEventLogger(buildPlpProductCardClickedPayload(product));
      analytics.trackEvent('View Details Clicked', {
        'Product Id': product.designId,
        'Product Name': product.designName,
        'MRP': parsePrice(product.price),
        'Selling Price': parsePrice(product.discountedPrice ?? product.price),
      });
    },
    [analytics],
  );

  const trackListingPageProductViewed = useCallback(
    (params: {
      product: ListingDesignItem;
      scrollDepth: number;
      horizontalPosition: number;
    }) => {
      if (!analytics) return;
      analytics.logEventLogger(buildListingPageProductViewedPayload(params));

      analytics.trackEvent('Product Viewed', {
        'Product Id': params.product.designId ? String(params.product.designId) : '',
        'Product Name': params.product.designName || '',
        'Category Name': params.product.categoryType || '',
        'MRP': params.product.price ? parsePrice(params.product.price) : 0,
        'Selling Price': params.product.discountedPrice ? parsePrice(params.product.discountedPrice) : 0,
        'Discount Price': params.product.discountValue ? parsePrice(params.product.discountValue) : 0,
        'Discount Percent': params.product.discountPercent ? parsePrice(String(params.product.discountPercent)) : 0,
        'Currency': 'INR',
        'Scroll depth': params.scrollDepth,
        'Horizontal position': params.horizontalPosition,
        'Offer Name': params.product.discountName ? params.product.discountName : '',
      });
    },
    [analytics],
  );

  // Video Call sticky bar CTA — book or close
  const trackVideoCallStickyBarClick = useCallback(
    (trialCartItemCount: number, buttonClicked: 'book_videocall' | 'cross') => {
      if (!analytics) return;
      analytics.logEventLogger({
        _eventId: 'plp_book_videocall_clicked',
        trial_cart_item_count: trialCartItemCount,
        button_clicked: buttonClicked,
      });
    },
    [analytics],
  );

  return {
    isReady: !!analytics,
    viewedProductIds,
    trackPageView,
    trackScrollDown,
    trackProductClick,
    trackVideoClick,
    trackWishlistClick,
    trackWishlistToggle,
    trackFilterItemSelected,
    trackDesktopFilterItemSelected,
    trackFilterOptionToggle,
    trackFilterApply,
    trackFilterReset,
    trackFilterClose,
    trackFilterCategoryExpand,
    trackSortOptionSelect,
    trackSortApply,
    trackSortCancel,
    trackPincodeClick,
    trackPincodeEntered,
    trackSortFilterClicked,
    trackFilterButtonClicked,
    trackSortFilterHovered,
    trackDesignsInStoreClick,
    trackVideoCallCartAdd,
    trackViewDetailsClick,
    trackListingPageProductViewed,
    trackVideoCallStickyBarClick,
  };
}
